import {
	Box,
	Button,
	CardActions,
	CardContent,
	Divider,
	FormHelperText,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	set,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { Form, useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import {
	useCreateUserListMutation,
	useEditUserMutation,
	useGetUserDetailQuery,
} from '@/api/user-management/userManagementListApiSlice';
import {
	StaffSimpleDetail,
	StaffSimpleDetailDto,
} from '@/api/DTO/user-management/userManagementList.interface';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
	useCheckInterventionMutation,
	useCreateInterventionMutation,
	useEditCaseNoteMutation,
	useEditInterventionMutation,
	useGetInterventionDetailQuery,
	useGetMemberTreeQuery,
	useLazyCheckInterventionChangeStatusQuery,
} from '@/api/member-management/intervention';
import ChangeMemberStatusDialog, {
	ADVANCE_TO_INTAKE_COMPLETED,
	REVERT_TO_PENDING_INTAKE,
} from './ChangeMemberStatusDialog';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import {
	useGetAllEnumListQuery,
	useGetDicListQuery,
	useGetDictByPcodeQuery,
	useGetFamilyCarersMemberListQuery,
	useGetStaffAllListQuery,
} from '@/api/common';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useSelector } from 'react-redux';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import AppTimePicker from '@/common/components/form-inputs/AppTimePicker';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import { skipToken } from '@reduxjs/toolkit/query';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { InterventionRequest } from '@/api/DTO/member-management/intervention';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';

import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { useGetAdmissionRequestDetailQuery } from '@/api/member-management/admissionRequest';
import { PermissionEnum } from '@/api/enum/common.enum';
import AddFamilyCarerDialog from './AddFamilyCarerDialog';
interface Props {
	type: FormType;
	editType?: string;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function InterventionForm({
	type,
	setType,
	editType,
	details,
}: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [files, setFiles] = useState(null);
	// Which field opened the "new family / carer" dialog, so the created carer
	// can be selected back into it. null = dialog closed.
	const [addFamilyCarerField, setAddFamilyCarerField] = useState<
		'involvingFamilyCarersNames' | 'involvingChildrenNames' | null
	>(null);
	const [createIntervention] = useCreateInterventionMutation();
	const [checkInterventionMutation] = useCheckInterventionMutation();
	const [editIntervention] = useEditInterventionMutation();
	const [editCaseNote] = useEditCaseNoteMutation();
	const [checkChangeStatus] = useLazyCheckInterventionChangeStatusQuery();
	const { myCaseId, interventionPage, admissionRequestId, interventionId } =
		useParams();
	const showModal = useModal();

	const userInfo = useSelector(selectMyInfo);

	const { data: admissionRequestDetails } = useGetAdmissionRequestDetailQuery(
		myCaseId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !(myCaseId && type == FormType.create),
		}
	);
	console.log(myCaseId, type);

	const { data: staffList }: any = useGetStaffAllListQuery(
		userInfo?.currentBranch?.id || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !userInfo?.currentBranch,
		}
	);

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: natureDicList } = useGetDicListQuery('nature', {
		refetchOnMountOrArgChange: true,
	});
	const { data: familyInvolvedAlliedHealthServicesEscortDicList } =
		useGetDicListQuery('family_involved_allied_health_services_escort', {
			refetchOnMountOrArgChange: true,
		});
	const { data: linkMhsDicList } = useGetDicListQuery('link_up_to_mhs', {
		refetchOnMountOrArgChange: true,
	});
	const { data: followClinicDicList } = useGetDicListQuery(
		'follow_up_clinic_psychiatry',
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: linkSsnDicList } = useGetDicListQuery('link_up_to_ssn', {
		refetchOnMountOrArgChange: true,
	});

	const { data: memberTreeList }: any = useGetMemberTreeQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const defaultValues: DefaultValues<InterventionRequest> = {
		userId: myCaseId || '',
		caseNoteType: -1,
	};

	const formSchema = Yup.object().shape({
		userId: Yup.string().required(t('errorMessage.pleaseSelect')),
		interventionWorkerId: Yup.string().required(t('errorMessage.pleaseSelect')),
		interventionDate: Yup.string()
			.required(t('errorMessage.pleaseSelect'))
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		nature: Yup.string().required(t('errorMessage.pleaseSelect')),
		caseNoteType: Yup.string()
			.required(t('errorMessage.pleaseSelect'))
			.notOneOf(['-1'], t('errorMessage.pleaseSelect')),

		involvingFamilyCarersNames: Yup.array().when(
			'familyInvolvedAlliedHealthServicesEscort',
			{
				is: (familyInvolvedAlliedHealthServicesEscort: string) =>
					familyInvolvedAlliedHealthServicesEscort &&
					involvingFamilyCarersListDisable(),
				then: (schema) =>
					schema
						.min(1, t('errorMessage.pleaseSelect'))
						.required(t('errorMessage.pleaseSelect')),
				otherwise: (schema) => schema.nullable(),
			}
		),
		involvingChildrenNames: Yup.array().when(
			'familyInvolvedAlliedHealthServicesEscort',
			{
				is: (familyInvolvedAlliedHealthServicesEscort: string) =>
					familyInvolvedAlliedHealthServicesEscort &&
					involvingChildrenListDisable(),
				then: (schema) =>
					schema
						.min(1, t('errorMessage.pleaseSelect'))
						.required(t('errorMessage.pleaseSelect')),
				otherwise: (schema) => schema.nullable(),
			}
		),
		riskLevel: Yup.string().when('nature', {
			is: (nature: string) => nature && riskLevelDisable(),
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		linkUpToMhs: Yup.string().when('nature', {
			is: (nature: string) => nature && linkUpToMhsDisable(),
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		followUpClinic: Yup.string().when('nature', {
			is: (nature: string) => nature && linkUpToMhsDisable(),
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		linkUpToSsn: Yup.string().when('nature', {
			is: (nature: string) => nature && linkUpToSsnDisable(),
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		linkUpSocialServiceProject: Yup.string().when('nature', {
			is: (nature: string) => nature && linkUpToSsnDisable(),
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		caseNoteStatus: Yup.string().when('caseNoteType', {
			is: (caseNoteType: string) => caseNoteType == '1' || caseNoteType == '2',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		caseNoteDetails: Yup.string().when('caseNoteType', {
			is: (caseNoteType: string) => caseNoteType == '1',
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		files: Yup.mixed().when('caseNoteType', {
			is: (caseNoteType: string) => caseNoteType === '2',
			then: (schema) =>
				schema
					.required(t('errorMessage.pleaseSelect'))
					.test('files-valid', t('errorMessage.pleaseSelect'), (value) => {
						if (value === null || value === undefined) return false;
						if (typeof value === 'string' && value.trim() === '') return false;
						return true;
					}),
			otherwise: (schema) => schema.nullable().notRequired(),
		}),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		if (type == FormType.edit) {
			methods.reset(details?.data);

			if (details?.data?.caseNoteType == 2) {
				methods.setValue('files', { name: details?.data.fileName });
				// setFiles(details?.data.fileName);
			}
		}
	}, [details]);

	useEffect(() => {
		if (type == FormType.create) methods.setValue('userId', myCaseId);
	}, [myCaseId]);
	useEffect(() => {
		if (type == FormType.create)
			methods.setValue('interventionWorkerId', userInfo?.id);
	}, [userInfo]);

	const watchFamilyInvolvedAlliedHealthServicesEscort = methods.watch(
		'familyInvolvedAlliedHealthServicesEscort'
	);
	const watchUserId = methods.watch('userId');
	const watchNature = methods.watch('nature');
	const watchAdditionalList = methods.watch('additionalList');
	const watchLinkUpToSsn = methods.watch('linkUpToSsn');
	const watchCaseNoteType = methods.watch('caseNoteType');

	const filesField = useController({
		name: 'files',
		control: methods.control,
	});

	const watchFiles = methods.watch('files');

	const { data: linkUpSocialServiceProjectList }: any = useGetDictByPcodeQuery(
		{
			type: 'link_up_social_service_project',
			pid:
				watchLinkUpToSsn &&
				linkSsnDicList?.data.find((item: any) => item.id == watchLinkUpToSsn)
					.id,
		},
		{
			refetchOnMountOrArgChange: true,
			skip:
				!watchLinkUpToSsn ||
				!linkSsnDicList?.data?.find((item: any) => item.id == watchLinkUpToSsn),
		}
	);

	const { data: involvingFamilyCarersList } = useGetFamilyCarersMemberListQuery(
		watchUserId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !watchUserId,
		}
	);

	// Both involving fields store a snapshot of the carer's name, so the option
	// value is the name itself. Carers sharing a name collapse into one option -
	// a name snapshot cannot tell them apart anyway.
	const familyCarerOptions = useMemo(() => {
		const seen = new Set<string>();
		return (involvingFamilyCarersList?.data || [])
			.filter((item: any) => {
				if (!item.familyCarerName || seen.has(item.familyCarerName))
					return false;
				seen.add(item.familyCarerName);
				return true;
			})
			.map((item: any) => ({
				...item,
				id: item.familyCarerName,
				name: item.familyCarerName,
			}));
	}, [involvingFamilyCarersList]);

	useEffect(() => {
		const subscription = methods.watch((value, field) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<InterventionRequest> = async (data) => {
		const obj: any = { ...data, userId: data.userId || myCaseId || '' };

		// The involving fields are name snapshots now. The id lists only ride
		// along from the detail response on edit, so drop them before submitting.
		delete obj.involvingFamilyCarersList;
		delete obj.involvingChildrenList;

		if (type == FormType.edit && editType == 'detail') {
			const o = { ...obj };
			delete o.files;
			await submitDetail(obj);
			return;
		}

		delete obj.files;
		const formData = new FormData();
		const appendObjToFormData = (
			formData: FormData,
			obj: any,
			parentKey = ''
		) => {
			for (const key in obj) {
				if (Object.prototype.hasOwnProperty.call(obj, key)) {
					const value = obj[key];
					const fullKey = parentKey ? `${parentKey}[${key}]` : key;

					if (value === null || value === undefined) {
						formData.append(fullKey, '');
					} else if (
						typeof value === 'object' &&
						!Array.isArray(value) &&
						value !== null
					) {
						appendObjToFormData(formData, value, fullKey);
					} else if (Array.isArray(value)) {
						value.forEach((item, index) => {
							if (typeof item === 'object' && item !== null) {
								appendObjToFormData(formData, item, `${fullKey}[${index}]`);
							} else {
								const itemValue =
									item === null || item === undefined ? '' : String(item);
								formData.append(`${fullKey}[]`, itemValue);
							}
						});
					} else {
						formData.append(fullKey, String(value));
					}
				}
			}
		};
		appendObjToFormData(formData, obj);
		if (files) {
			formData.append('files', files);
		}

		if (type == FormType.edit && editType == 'note') {
			submitForm({ formData: formData, id: obj.id });
			return;
		}

		const response = await checkInterventionMutation(formData);
		if ('data' in response) {
			if (!response.data?.data || response.data?.data.length < 1) {
				submitForm(formData);
			} else {
				showModal((props: any) => (
					<ConfirmationDialog
						{...props}
						showCloseIcon={false}
						title={t('case.interventionTipTitle')}
						titleProps={{ variant: 'body1' }}
						description={
							<Box>
								<Typography variant='body1'>
									{t('case.interventionTipText')}
								</Typography>
								{response.data?.data.map((item: any) => (
									<>
										<Button
											key={item.id}
											component='label'
											role={undefined}
											variant='outlined'
											tabIndex={-1}
											sx={{ border: 'none', display: 'block' }}
										>
											<a
												style={{ color: '#1BBED7' }}
												href={`	/case-management/centre-intervention/detail/${item.id}`}
												target='_blank'
												rel='noopener noreferrer'
											>
												{t('button.viewDetails')}
											</a>
										</Button>
									</>
								))}
							</Box>
						}
						cancellationText={t('button.cancel')}
						confirmationText={t('button.confirmSubmit')}
						confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
						onConfirm={async () => {
							submitForm(formData);
						}}
					/>
				));
			}
		}
	};

	const natureLabel = (natureId: string) =>
		natureDicList?.data.find((item: { id: any }) => item.id === natureId)
			?.labelEn;

	// Editing the nature can move the member's status with it. The backend never
	// does that on its own, so ask the staff first and pass their answer along.
	const submitDetail = async (obj: any) => {
		const check = await checkChangeStatus({ id: obj.id, nature: obj.nature });
		const direction = check.data?.data ?? 0;

		if (
			direction != REVERT_TO_PENDING_INTAKE &&
			direction != ADVANCE_TO_INTAKE_COMPLETED
		) {
			submitForm(obj);
			return;
		}

		showModal((props: any) => (
			<ChangeMemberStatusDialog
				{...props}
				direction={direction}
				// Reverting is about the nature being replaced, advancing about the
				// newly picked one.
				nature={
					direction == REVERT_TO_PENDING_INTAKE
						? details?.data.natureDesc
						: natureLabel(obj.nature)
				}
				onSave={(changeStatus: boolean) => submitForm(obj, changeStatus)}
			/>
		));
	};

	const submitForm = async (formData: any, changeStatus?: boolean) => {
		const r =
			type == FormType.create
				? await createIntervention(formData)
				: editType == 'detail'
					? await editIntervention({ body: formData, changeStatus })
					: await editCaseNote(formData);

		if ('data' in r) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createInterventionSuccess')
					: t('snackbar.editInterventionSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (methods.getValues('buttonType') == 2) {
				navigate(0);
			} else {
				routerTo();
			}
		}
	};

	const routerTo = () => {
		if (interventionPage == 'case-all') {
			navigate('/case-management/my-case');
		}
		if (interventionPage == 'case-pending') {
			navigate('/case-management/my-case/pending');
		}
		if (interventionPage == 'case-relieving') {
			navigate('/case-management/my-case/relieving');
		}
		if (interventionPage == 'case-shared') {
			navigate('/case-management/my-case/shared');
		}
		if (interventionPage == 'centre-intervention') {
			navigate('/case-management/centre-intervention');
		}
		if (interventionPage == 'centre-case-admitted') {
			navigate('/case-management/centre-case');
		}
		if (interventionPage == 'centre-case-not-admitted') {
			navigate('/case-management/centre-case/not-admitted');
		}
		if (interventionPage == 'centre-case-close') {
			navigate('/case-management/centre-case/close');
		}
		if (interventionPage == 'program-all') {
			navigate('/case-management/my-program');
		}
		if (interventionPage == 'program-pending') {
			navigate('/case-management/my-program/program-pending');
		}
		if (interventionPage == 'program-staff') {
			navigate('/case-management/my-program/program-staff');
		}
		if (interventionPage == 'centre-program') {
			navigate('/case-management/centre-program');
		}
		if (interventionPage == 'centre-program-not') {
			navigate('/case-management/centre-program/not-admitted');
		}
		if (interventionPage == 'centre-intervention') {
			navigate('/case-management/centre-intervention');
		}
		if (interventionPage == 'my-intervention') {
			navigate('/case-management/intervention');
		}
		if (interventionPage == 'my-intervention-submitter') {
			navigate('/case-management/intervention/submitter');
		}
		if (interventionPage == 'member-detail') {
			navigate(
				`/member-management/member-list/detail/${myCaseId}?tab=intervention`
			);
		}
		if (interventionPage == 'admission-detail') {
			navigate(
				`/member-management/admission-request-list/detail/${myCaseId}?tab=intervention`
			);
		}

		// The edit pages sit under the intervention detail route, so the id comes
		// from :interventionId (:myCaseId only exists on the create routes) and
		// each edit page returns to the detail it was opened from.
		if (interventionPage == 'edit-centre-intervention') {
			navigate(`/case-management/centre-intervention/detail/${interventionId}`);
		}
		if (interventionPage == 'edit-my-intervention') {
			navigate(`/case-management/intervention/detail/${interventionId}`);
		}
		if (interventionPage == 'edit-member-intervention') {
			navigate(
				`/member-management/member-list/detail/${admissionRequestId}/intervention/detail/${interventionId}`
			);
		}
		if (interventionPage == 'edit-admission-intervention') {
			navigate(
				`/member-management/admission-request-list/detail/${admissionRequestId}/intervention/detail/${interventionId}`
			);
		}
	};

	const handleClose = () => {
		routerTo();
	};

	const involvingFamilyCarersListDisable = () => {
		const obj = familyInvolvedAlliedHealthServicesEscortDicList?.data.find(
			(item: { id: any }) =>
				item.id === watchFamilyInvolvedAlliedHealthServicesEscort
		);
		if (!obj) return false;
		if (obj.code === '1' || obj.code === '3') {
			return true;
		}
		return false;
	};
	const involvingChildrenListDisable = () => {
		const obj = familyInvolvedAlliedHealthServicesEscortDicList?.data.find(
			(item: { id: any }) =>
				item.id === watchFamilyInvolvedAlliedHealthServicesEscort
		);
		if (!obj) return false;
		if (obj.code === '2') {
			return true;
		}
		return false;
	};

	const canEditFamilyCarers = userInfo?.permissionList?.includes(
		PermissionEnum.EditAdmissionRequestAndMember
	);

	// The family/carer list is maintained on the member's record. When a
	// selected option requires picking someone from it, offer an "add new" row
	// inside the dropdown so a missing person can be added without leaving the
	// intervention form.
	const familyCarerAddNewOption = (
		field: 'involvingFamilyCarersNames' | 'involvingChildrenNames'
	) => {
		if (!watchUserId) return undefined;
		return {
			text: t('button.newFamilyCarer'),
			disabled: !canEditFamilyCarers,
			onClick: () => setAddFamilyCarerField(field),
		};
	};

	const riskLevelDisable = () => {
		const obj = natureDicList?.data.find(
			(item: { id: any }) => item.id === watchNature
		);
		if (!obj) return false;
		if (
			['1', '2', '3', '4', '5', '6', '7', '9', '10', '11', '12']?.includes(
				obj.code
			)
		) {
			return true;
		}
		return false;
	};
	const linkUpToMhsDisable = () => {
		const obj = natureDicList?.data.find(
			(item: { id: any }) => item.id === watchNature
		);
		if (
			(obj && obj.code === '17') ||
			(watchAdditionalList && watchAdditionalList?.includes(2))
		) {
			return true;
		}
		return false;
	};
	const linkUpToSsnDisable = () => {
		const obj = natureDicList?.data.find(
			(item: { id: any }) => item.id === watchNature
		);
		if (
			(obj && obj.code === '18') ||
			(watchAdditionalList && watchAdditionalList?.includes(3))
		) {
			return true;
		}
		return false;
	};

	const handleUpload = (file: any) => {
		methods.setValue('files', file[0]);
		setFiles(file[0]);
		methods.trigger();
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						{!editType && (
							<>
								{' '}
								<Typography
									variant='body1'
									sx={{ fontWeight: 'bold' }}
									marginBottom={2}
								>
									{t('member.personalInformation')}
								</Typography>
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid size={6}>
										{myCaseId ? (
											<AppAutoCompleteMenu
												disabled={myCaseId ? true : false}
												control={methods.control}
												name={'userId'}
												label={t('preAdmission.name') + '*'}
												options={[
													{
														id: admissionRequestDetails?.data.id,
														name:
															admissionRequestDetails?.data.nameEn +
															' ' +
															admissionRequestDetails?.data.nameZh,
													},
												]}
											/>
										) : (
											<AppAutoCompleteMenu
												disabled={myCaseId ? true : false}
												control={methods.control}
												name={'userId'}
												label={t('preAdmission.name') + '*'}
												options={(memberTreeList?.data || []).map(
													(item: any) => {
														const childrenList = (item.childrenList || []).map(
															(i: any) => {
																return {
																	...i,
																	name: i.nameEn + ' ' + i.nameZh,
																};
															}
														);
														return {
															...item,
															childrenList: childrenList,
															name: item.label,
														};
													}
												)}
											/>
										)}
									</Grid>
								</Grid>
								<Divider sx={{ margin: '24px 0' }} />
							</>
						)}
						{(editType == 'detail' || !editType) && (
							<>
								<Typography
									variant='body1'
									sx={{ fontWeight: 'bold' }}
									marginBottom={2}
								>
									{t('case.interventionInformation')}
								</Typography>
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='interventionWorkerId'
											label={t('case.interventionWorkerField') + '*'}
											options={staffList?.data || []}
										/>
									</Grid>
									<Grid size={6}></Grid>
									<Grid size={6}>
										<AppDatePicker
											maximumDate={dayjs()
												.startOf('day')
												.format(API_DATE_FORMAT)}
											control={methods.control}
											name='interventionDate'
											label={t('case.date') + '*'}
										/>
									</Grid>
									<Grid size={6}></Grid>
									<Grid size={6}>
										<AppTimePicker
											control={methods.control}
											name='interventionStartTime'
											label={t('case.startTime')}
										/>
									</Grid>
									<Grid size={6}>
										<AppTimePicker
											control={methods.control}
											name='interventionEndTime'
											label={t('case.endTime')}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											control={methods.control}
											name='nature'
											label={t('member.nature') + '*'}
											options={(natureDicList?.data || []).map(
												(item: { labelEn: any }) => {
													return {
														...item,
														name: item.labelEn,
													};
												}
											)}
											handleChange={() => {
												methods.setValue('riskLevel', '');
												methods.setValue('linkUpToMhs', '');
												methods.setValue('followUpClinic', '');
												methods.setValue('linkUpToSsn', '');
												methods.setValue('linkUpSocialServiceProject', '');
												methods.setValue(
													'linkUpSocialServiceOtProjectSpecify',
													''
												);

												// methods.trigger('riskLevel');
												// methods.trigger('linkUpToMhs');
												// methods.trigger('followUpClinic');
												// methods.trigger('linkUpToSsn');
												// methods.trigger('linkUpSocialServiceProject');
												// methods.trigger('linkUpSocialServiceOtProjectSpecify');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppMultipleDropDownMenu
											control={methods.control}
											name='additionalList'
											label={t('case.additional')}
											options={(allEnumList?.data.AdditionalEnum || []).map(
												(item: any) => {
													return {
														...item,
														id: item.code,
														name: item.desc,
													};
												}
											)}
											handleChange={() => {
												methods.setValue('linkUpToMhs', '');
												methods.setValue('followUpClinic', '');
												methods.setValue('linkUpToSsn', '');
												methods.setValue('linkUpSocialServiceProject', '');
												methods.setValue(
													'linkUpSocialServiceOtProjectSpecify',
													''
												);

												// methods.trigger('linkUpToMhs');
												// methods.trigger('followUpClinic');
												// methods.trigger('linkUpToSsn');
												// methods.trigger('linkUpSocialServiceProject');
												// methods.trigger('linkUpSocialServiceOtProjectSpecify');
											}}
										/>
									</Grid>

									<Grid size={6}>
										<AppDropDownMenu
											control={methods.control}
											name='familyInvolvedAlliedHealthServicesEscort'
											label={t('case.familyInvolvedAlliedHealthServicesEscort')}
											options={(
												familyInvolvedAlliedHealthServicesEscortDicList?.data ||
												[]
											).map((item: any) => {
												return {
													...item,
													name: item.labelEn,
												};
											})}
											handleChange={() => {
												methods.setValue('involvingFamilyCarersNames', []);
												methods.setValue('involvingChildrenNames', []);

												// methods.trigger('involvingFamilyCarersNames');
												// methods.trigger('involvingChildrenNames');
											}}
										/>
									</Grid>
									<Grid size={6}></Grid>

									<Grid size={6}>
										<AppMultipleDropDownMenu
											disabled={!involvingFamilyCarersListDisable()}
											control={methods.control}
											name='involvingFamilyCarersNames'
											label={`${t('case.involvingFamiliesCarers')}${
												!involvingFamilyCarersListDisable() ? '' : '*'
											}`}
											options={familyCarerOptions}
											showUnmatchedValues
											unmatchedValueSuffix={t('case.familyCarerNoLongerListed')}
											addNewOption={familyCarerAddNewOption(
												'involvingFamilyCarersNames'
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppMultipleDropDownMenu
											disabled={!involvingChildrenListDisable()}
											control={methods.control}
											name='involvingChildrenNames'
											label={`${t('case.involvingChildren')}${
												!involvingChildrenListDisable() ? '' : '*'
											}`}
											options={familyCarerOptions}
											showUnmatchedValues
											unmatchedValueSuffix={t('case.familyCarerNoLongerListed')}
											addNewOption={familyCarerAddNewOption(
												'involvingChildrenNames'
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											disabled={!riskLevelDisable()}
											control={methods.control}
											name='riskLevel'
											label={`${t('case.riskLevel')}${
												!riskLevelDisable() ? '' : '*'
											}`}
											options={(allEnumList?.data.RiskLevelEnum || []).map(
												(item: any) => {
													return {
														...item,
														id: item.code,
														name: item.desc,
													};
												}
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppMultipleDropDownMenu
											control={methods.control}
											name='supportingStaffList'
											label={t('case.supportingStaff')}
											options={staffList?.data || []}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											disabled={!linkUpToMhsDisable()}
											control={methods.control}
											name='linkUpToMhs'
											label={`${t('case.linkUpMHS')}${
												!linkUpToMhsDisable() ? '' : '*'
											}`}
											options={(linkMhsDicList?.data || []).map(
												(item: { labelEn: any }) => {
													return {
														...item,
														name: item.labelEn,
													};
												}
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											disabled={!linkUpToMhsDisable()}
											control={methods.control}
											name='followUpClinic'
											label={`${t('case.followUpClinic')}${
												!linkUpToMhsDisable() ? '' : '*'
											}`}
											options={(followClinicDicList?.data || []).map(
												(item: { labelEn: any }) => {
													return {
														...item,
														name: item.labelEn,
													};
												}
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											disabled={!linkUpToSsnDisable()}
											control={methods.control}
											name='linkUpToSsn'
											label={`${t('case.linkUpSSN')}${
												!linkUpToSsnDisable() ? '' : '*'
											}`}
											options={(linkSsnDicList?.data || []).map(
												(item: { labelEn: any }) => {
													return {
														...item,
														name: item.labelEn,
													};
												}
											)}
											handleChange={() => {
												methods.setValue('linkUpSocialServiceProject', '');
												methods.trigger('linkUpSocialServiceProject');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											disabled={!linkUpToSsnDisable()}
											control={methods.control}
											name='linkUpSocialServiceProject'
											label={`${t('case.linkUpSocialServiceProject')}${
												!linkUpToSsnDisable() ? '' : '*'
											}`}
											options={(linkUpSocialServiceProjectList?.data || []).map(
												(item: { labelEn: any }) => {
													return {
														...item,
														name: item.labelEn,
													};
												}
											)}
											handleChange={() => {
												methods.trigger('linkUpSocialServiceProject');
											}}
										/>
									</Grid>
									<Grid size={12}>
										<AppTextField
											disabled={!linkUpToSsnDisable()}
											control={methods.control}
											name='linkUpSocialServiceOtProjectSpecify'
											label={`${t('case.linkUpSocialServiceProjectSpecify')}`}
										/>
									</Grid>
								</Grid>
								<Divider sx={{ margin: '24px 0' }} />
							</>
						)}

						{(editType == 'note' || !editType) && (
							<>
								<Typography
									variant='body1'
									sx={{ fontWeight: 'bold' }}
									marginBottom={2}
								>
									{t('case.caseNote')}
								</Typography>
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid size={12}>
										<Typography variant='body2' color='common.neutral40'>
											{t('case.caseNoteType') + '*'}
										</Typography>
										<AppRadioGroup
											control={methods.control}
											name={`caseNoteType`}
											row
											options={(allEnumList?.data.CaseNoteTypeEnum || []).map(
												(item: any) => {
													return {
														...item,
														id: item.code,
														name: item.desc,
													};
												}
											)}
											handleChange={() => {
												// methods.trigger('caseNoteStatus');
												// methods.trigger('caseNoteDetails');
												methods.setValue('files', '');
												setFiles(null);
												methods.setValue('caseNoteStatus', '');
												methods.setValue('caseNoteDetails', '');
												methods.trigger('files');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											disabled={
												watchCaseNoteType != 1 && watchCaseNoteType != 2
											}
											control={methods.control}
											name='caseNoteStatus'
											label={`${t('case.caseNoteStatus')}${
												watchCaseNoteType == 1 || watchCaseNoteType == 2
													? '*'
													: ''
											}`}
											options={[
												{
													id: 1,
													name: 'Draft',
												},
												{
													id: 2,
													name: 'Submitted',
												},
											]}
										/>
									</Grid>
									<Grid size={12}>
										<AppTextField
											disabled={watchCaseNoteType != 1}
											control={methods.control}
											multiline
											name='caseNoteDetails'
											label={t('case.caseNoteDetails')}
											rows={4}
										/>
									</Grid>
									<Grid size={12}>
										<Box sx={{ margin: '0px 0 10px' }}>
											<Typography variant='body2' color='common.neutral40'>
												{`${t('preAdmission.selectDocuments')}${
													watchCaseNoteType == 2 ? '*' : ''
												}`}
											</Typography>
											{!watchFiles && (
												<Button
													disabled={watchCaseNoteType != 2}
													component='label'
													role={undefined}
													variant='outlined'
													tabIndex={-1}
													endIcon={<FileUploadOutlinedIcon />}
													sx={{ border: 'none' }}
												>
													{t('button.UploadFile')}
													<VisuallyHiddenInput
														type='file'
														onChange={(event: any) =>
															handleUpload(event.target.files)
														}
													/>
												</Button>
											)}
											{watchFiles && (
												<Box
													display={'flex'}
													alignItems={'center'}
													paddingLeft={'15px'}
												>
													<Typography
														variant='body1'
														sx={{
															color: '#1BBED7',
															textDecoration: 'underline',
														}}
													>
														{watchFiles.name}
													</Typography>
													<IconButton
														disabled={watchCaseNoteType != 2}
														sx={{ color: '#1BBED7' }}
														onClick={() => {
															methods.setValue('files', null);
															setFiles(null);
														}}
													>
														<CancelOutlinedIcon />
													</IconButton>
												</Box>
											)}
											<FormHelperText
												color='error'
												style={{ color: '#FF5B4D' }}
											>
												{filesField.fieldState.error
													? t('errorMessage.pleaseSelect')
													: ''}
											</FormHelperText>
										</Box>
									</Grid>
								</Grid>
							</>
						)}
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							endIcon={<CheckCircleOutlineIcon />}
							onClick={() => {
								methods.setValue('buttonType', 1);
								methods.handleSubmit(onSubmit);
							}}
						>
							{t('button.complete')}
						</Button>
						{type == FormType.create && (
							<Button
								type='submit'
								variant='contained'
								endIcon={<EditNoteOutlinedIcon />}
								onClick={() => {
									methods.setValue('buttonType', 2);
									methods.handleSubmit(onSubmit);
								}}
							>
								{t('button.completeAndCreateAnother')}
							</Button>
						)}
					</CardActions>
				</form>
			</FormProvider>
			<AddFamilyCarerDialog
				open={!!addFamilyCarerField}
				userId={watchUserId || ''}
				onClose={() => setAddFamilyCarerField(null)}
				onCreated={(newCarerName: string) => {
					if (!addFamilyCarerField) return;
					methods.setValue(
						addFamilyCarerField,
						[...(methods.getValues(addFamilyCarerField) || []), newCarerName],
						{ shouldValidate: true }
					);
				}}
			/>
		</>
	);
}
